import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import type { MerchantListItemDto } from "Infrastructure/Api/Api";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { type TFunction, Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const createColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<MerchantListItemDto>> => [
  {
    accessorKey: "merchantStatus",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.Status),
    Cell: ({ row }) => (
      <MerchantStatusCell status={row.original.merchantStatus} />
    ),
    filterVariant: "multi-select",
    filterSelectOptions: [
      {
        label: t(Resources.Merchant.MerchantStatus.InDraft),
        value: "InDraft",
      },
      {
        label: t(
          Resources.Merchant.MerchantStatus.InvitedWaitingForConfirmation,
        ),
        value: "InvitedWaitingForConfirmation",
      },
      {
        label: t(Resources.Merchant.MerchantStatus.Onboarding),
        value: "Onboarding",
      },
      {
        label: t(Resources.Merchant.MerchantStatus.WaitingForPaymentTerminal),
        value: "WaitingForPaymentTerminal",
      },
      { label: t(Resources.Merchant.MerchantStatus.Active), value: "Active" },
      {
        label: t(Resources.Merchant.MerchantStatus.BlockedFinancialServices),
        value: "BlockedFinancialServices",
      },
    ],
    size: 0,
  },
  {
    accessorKey: "party.companyName",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.CompanyName),
    size: 200,
  },
  {
    accessorKey: "party.companyNumber",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.CompanyNumber),
    size: 110,
  },
  {
    accessorKey: "dateCreated",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.DateCreated),
    size: 150,
    Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
  },
];

type Props = {
  data: Array<MerchantListItemDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  onRowClick?: (merchant: MerchantListItemDto) => void;
};

export const MerchantSelectionTable = ({
  data,
  isRefetching,
  isLoading,
  onRowClick,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const columns = createColumns(t);

  const table = useMaterialReactTable({
    data,
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      density: "compact",
      pagination: { pageSize: 15, pageIndex: 0 },
      sorting: [{ id: "dateCreated", desc: true }],
      columnVisibility: {
        dateCreated: false,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (_e) => {
        onRowClick?.(row.original);
      },
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
